// CHARGEBACKS - the customer service log of every card dispute, Crab Place and Crab & Cruise.
//
// Rachael's spec, 2026-10-15. This replaces her "CP Chargebacks" spreadsheet, and it is the screen
// for the ONE production record: crabplaceNEW.dbo.Chargebacks, read here through its Neon mirror and
// written through the Common BE (netlify/functions/lib/chargebacks.js). Nothing on this page writes
// to Neon directly - a save goes to production and the mirror is re-pulled before the page reloads.
//
// ⚠️ WHY THIS MATTERS BEYOND CS. Authorize.Net cannot see a chargeback (the charge still reads
// settled, no refund ever appears), so this log is the only thing that stops a batch job treating a
// charged-back member as "paid and cancelled for no reason". On 2026-10-10 production reinstated 45
// of them because the record lived only in CPx. A membership chargeback here blocks VIP until it is
// won or somebody deliberately allows it ("Allow VIP again").
//
// ⚠️ ONE ROW PER DISPUTE. A dispute that goes round again (PreArb, a reversal, the customer
// challenging our rebuttal) is a FOLLOW-UP CASE LINE under the original, never a new chargeback -
// otherwise the same money is counted twice in the totals Greg and Mercedes post to QuickBooks.
//
// The codes (statuses, follow-ups, categories, case types) come from the server with the data, so
// the list Rachael adjusts lives in one place (CODES in lib/chargebacks.js).

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Loader, Plus, RefreshCw, X, StickyNote, Pencil, Trash2, CornerDownRight, ShieldOff, ShieldCheck, Download } from 'lucide-react';
import { neonQuery } from '../services/api';
import { getCurrentUser } from '../services/auth';

const DASH = String.fromCharCode(8212);
const money = (n) => (n == null || n === '' ? DASH
  : Number(n).toLocaleString('en-US', { style: 'currency', currency: 'USD' }));
const has = (n) => n != null && n !== '' && Number(n) !== 0;
// Dates arrive as yyyy-MM-dd (lib/chargebacks.js days()); shown US style.
const day = (s) => {
  if (!s) return DASH;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s));
  return m ? `${Number(m[2])}/${Number(m[3])}/${m[1]}` : String(s);
};
const todayIso = () => new Date().toLocaleDateString('en-CA');
const yearStart = () => `${new Date().getFullYear()}-01-01`;
const rep = () => { const u = getCurrentUser() || {}; return u.staffName || u.name || null; };

const LABEL = { fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 4, textTransform: 'uppercase' };
const TH = { textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)', whiteSpace: 'nowrap' };
const TD = { padding: '7px 8px', verticalAlign: 'top' };
const LINK_BTN = { border: 'none', background: 'none', color: 'var(--gray-500)', cursor: 'pointer', padding: 2, display: 'inline-flex', alignItems: 'center' };
const INPUT = { fontSize: 12, width: '100%', boxSizing: 'border-box' };

// Status badge colours by meaning, not by code: settled for us, settled against us, still moving.
const STATUS_TONE = {
  won: ['#e7f6ec', '#1e7b3a'],
  lost: ['#fdecea', '#b42318'],
  accepted: ['#fdecea', '#b42318'],
  lost_fighting: ['#fff4e5', '#b54708'],
  customer_challenged: ['#fff4e5', '#b54708'],
  rebutted: ['#e8f0fe', '#00395C'],
  awaiting: ['#e8f0fe', '#00395C'],
};

function Badge({ code, labels, small }) {
  if (!code) return null;
  const [bg, fg] = STATUS_TONE[code] || ['var(--gray-100)', 'var(--gray-700)'];
  return (
    <span style={{ fontSize: small ? 10 : 10.5, fontWeight: 700, padding: '2px 7px', borderRadius: 10, background: bg, color: fg, whiteSpace: 'nowrap' }}>
      {(labels && labels[code]) || code}
    </span>
  );
}

function Flag({ children, tone = 'gray', title }) {
  const tones = {
    red: ['#fdecea', '#b42318'], amber: ['#fff4e5', '#b54708'], gray: ['var(--gray-100)', 'var(--gray-600)'], green: ['#e7f6ec', '#1e7b3a'],
  };
  const [bg, fg] = tones[tone];
  return <span title={title} style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 8, background: bg, color: fg, whiteSpace: 'nowrap', display: 'inline-block', margin: '0 3px 3px 0' }}>{children}</span>;
}

function Modal({ title, onClose, children, width = 640 }) {
  return (
    <div onMouseDown={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(16,24,40,0.5)', zIndex: 4100, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 16px', overflowY: 'auto' }}>
      <div onMouseDown={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, width: `min(${width}px,100%)`, overflow: 'hidden' }}>
        <div style={{ padding: '12px 16px', background: 'var(--navy)', color: '#fff', display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ fontWeight: 800, fontSize: 14 }}>{title}</div>
          <div style={{ flex: 1 }} />
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', display: 'flex' }}><X size={18} /></button>
        </div>
        <div style={{ padding: '14px 16px' }}>{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children, span = 1 }) {
  return (
    <div style={{ gridColumn: `span ${span}` }}>
      <div style={LABEL}>{label}</div>
      {children}
    </div>
  );
}

// The existing CPx customer lookup (neon customer_search: email or name).
function CustomerPicker({ onPick, placeholder }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const term = q.trim();
    if (term.length < 3) { setResults([]); return undefined; }
    let live = true;
    const t = setTimeout(async () => {
      setBusy(true);
      try {
        const r = await neonQuery('customer_search', { q: term });
        if (live) setResults(Array.isArray(r) ? r.slice(0, 8) : []);
      } catch (_) { if (live) setResults([]); }
      if (live) setBusy(false);
    }, 300);
    return () => { live = false; clearTimeout(t); };
  }, [q]);
  return (
    <div style={{ position: 'relative' }}>
      <input className="form-input" style={INPUT} value={q} onChange={(e) => setQ(e.target.value)}
        placeholder={placeholder || 'Search name or email (3+ letters)'} />
      {busy && <Loader size={12} className="spin" style={{ position: 'absolute', right: 8, top: 8 }} />}
      {results.length > 0 && (
        <div style={{ position: 'absolute', zIndex: 5, left: 0, right: 0, background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', maxHeight: 240, overflowY: 'auto' }}>
          {results.map((c) => (
            <div key={c.id} onMouseDown={() => { onPick(c); setQ(''); setResults([]); }}
              style={{ padding: '6px 10px', fontSize: 12, cursor: 'pointer', borderBottom: '1px solid var(--gray-100)' }}>
              <b>{fullName(c) || '(no name)'}</b>
              <span style={{ color: 'var(--gray-500)' }}> {c.email || ''} · id {c.id} · {c.order_count || 0} orders</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const fullName = (c) => [c.first_name, c.last_name].filter(Boolean).join(' ').trim();

const FORM_DEFAULTS = {
  chargebackId: 0, company: 'CP', category: '', kind: 'order', customerId: null, customerName: '', email: '',
  orderId: null, orderNumber: '', transactionId: '', amount: '', amountDebited: '', amountCredited: '',
  transactionDate: '', receivedDate: '', caseNumber: '', caseType: '', reason: '', isFraud: '',
  status: 'rebutted', followUp: '', followUpDate: '', resold: false, notes: '',
};

function fromRow(r) {
  if (!r) return { ...FORM_DEFAULTS, receivedDate: todayIso() };
  return {
    chargebackId: r.chargeback_id, company: r.company || 'CP', category: r.category || '', kind: r.kind || 'order',
    customerId: r.customer_id || null, customerName: r.customer_name || '', email: r.email || '',
    orderId: r.order_id || null, orderNumber: r.order_number || '', transactionId: r.transaction_id || '',
    amount: r.amount ?? '', amountDebited: r.amount_debited ?? '', amountCredited: r.amount_credited ?? '',
    transactionDate: r.transaction_date || '', receivedDate: r.received_date || '', caseNumber: r.case_number || '',
    caseType: r.case_type || '', reason: r.reason || '',
    isFraud: r.is_fraud === true ? 'Y' : r.is_fraud === false ? 'N' : '',
    status: r.status || 'rebutted', followUp: r.follow_up || '', followUpDate: r.follow_up_date || '',
    resold: !!r.resold, notes: r.notes || '',
  };
}

function ChargebackForm({ row, codes, onClose, onSaved }) {
  const [f, setF] = useState(() => fromRow(row));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [orderHint, setOrderHint] = useState(null);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const isEdit = !!f.chargebackId;
  // ⚠️ A CUSTOMER IS LINKED ONCE (production ignores customerId on a chargeback that already has
  // one). An imported sheet line with no match arrives unlinked; the rep links it here, and from
  // then on it is read-only.
  const linkedOnServer = !!(row && row.customer_id);
  const moving = (codes.followUpStatuses || []).includes(f.status);
  const statusLabels = codes.statusLabels || {};

  const pick = (c) => setF((p) => ({ ...p, customerId: c.id, customerName: fullName(c) || p.customerName, email: c.email || p.email }));

  // Crab Place order number: resolve it against the orders mirror so production gets orders.orderid,
  // and fill the customer from it when none is chosen yet.
  // ⚠️ ANYTHING NOT ALL DIGITS IS A C&C REF (9-14 characters, never all digits), looked up in the C&C
  // orders mirror. A hit switches the company to C&C - a C&C order typed on a Crab Place line is how
  // 18 imported chargebacks ended up under Crab Place (Greg, 2026-10-27) - and stores the ref as C&C
  // prints it, since refs are case-sensitive.
  const lookupOrder = async () => {
    const q = String(f.orderNumber || '').trim();
    setOrderHint(null);
    if (!q) { set('orderId', null); return; }
    if (!/^d+$/.test(q)) return lookupCncOrder(q);
    if (f.company !== 'CP') { set('orderId', null); return; }
    try {
      const r = await neonQuery('customer_by_order', { q });
      const hit = Array.isArray(r) ? r[0] : null;
      if (!hit) { set('orderId', null); setOrderHint('Not found in CPx - saved as typed.'); return; }
      setF((p) => ({
        ...p,
        orderId: hit.matched_order_id ? Number(hit.matched_order_id) : null,
        ...(p.customerId || linkedOnServer || !hit.id ? {} : { customerId: hit.id, customerName: fullName(hit), email: hit.email || '' }),
      }));
      setOrderHint('Found order ' + (hit.matched_order_bc_id || q) + (hit.email ? ' (' + hit.email + ')' : ''));
    } catch (e) { setOrderHint('Could not look the order up: ' + e.message); }
  };

  // The name and email are filled only when the rep has not typed them, so a notice that names
  // someone else is kept as entered.
  const lookupCncOrder = async (q) => {
    try {
      const r = await neonQuery('cnc_order_by_ref', { q });
      const hit = Array.isArray(r) ? r[0] : null;
      if (!hit) {
        set('orderId', null);
        setOrderHint(f.company === 'CC' ? 'Not found in the C&C orders - saved as typed.' : 'Not found in CPx - saved as typed.');
        return;
      }
      const name = [hit.first_name, hit.last_name].filter(Boolean).join(' ');
      setF((p) => ({
        ...p,
        company: 'CC',
        orderId: null,
        orderNumber: hit.id,
        ...(p.customerId || linkedOnServer ? {} : {
          customerName: p.customerName || name,
          email: p.email || hit.email || '',
        }),
      }));
      setOrderHint('Found Crab & Cruise order ' + hit.id + (hit.email ? ' (' + hit.email + ')' : '')
        + (f.company === 'CC' ? '' : ' - company set to Crab & Cruise'));
    } catch (e) { setOrderHint('Could not look the order up: ' + e.message); }
  };

  const save = async () => {
    setErr(null);
    if (!isEdit && f.company === 'CP' && !f.customerId) {
      setErr('Choose the customer (search by name or email, or enter the order number first).');
      return;
    }
    setBusy(true);
    try {
      const r = await neonQuery('chargeback_save', { ...f, resold: f.company === 'CC' && f.resold === true, repName: rep() });
      if (r && r.ok === false) { setErr(r.reason || 'Not saved.'); setBusy(false); return; }
      onSaved(r);
    } catch (e) { setErr(e.message || 'Not saved.'); setBusy(false); }
  };

  const customerLine = (
    <div style={{ fontSize: 12.5, padding: '6px 0' }}>
      <b>{f.customerName || '(no name)'}</b> <span style={{ color: 'var(--gray-500)' }}>{f.email} · id {f.customerId}</span>
      {linkedOnServer
        ? <span style={{ fontSize: 11, color: 'var(--gray-400)', marginLeft: 8 }}>linked - to move it to someone else, remove this chargeback and enter it again</span>
        : <button style={{ ...LINK_BTN, marginLeft: 8, textDecoration: 'underline', fontSize: 11 }} onClick={() => set('customerId', null)}>change</button>}
    </div>
  );

  return (
    <Modal title={isEdit ? 'Edit chargeback' : 'Add chargeback'} onClose={onClose} width={760}>
      <ChargebackFields f={f} set={set} codes={codes} statusLabels={statusLabels} moving={moving}
        customer={f.customerId ? customerLine : (
          <div>
            {isEdit && (
              <div style={{ fontSize: 11.5, color: '#b54708', marginBottom: 4 }}>
                No customer linked{f.customerName || f.email ? ' (the sheet said: ' + [f.customerName, f.email].filter(Boolean).join(', ') + ')' : ''}.
                {f.kind === 'membership' ? ' It cannot block VIP until one is linked.' : ''}
              </div>
            )}
            <CustomerPicker onPick={pick} />
            {f.company === 'CC' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 6 }}>
                <input className="form-input" style={INPUT} placeholder="Or type the name" value={f.customerName} onChange={(e) => set('customerName', e.target.value)} />
                <input className="form-input" style={INPUT} placeholder="and email" value={f.email} onChange={(e) => set('email', e.target.value)} />
              </div>
            )}
          </div>
        )}
        orderHint={orderHint} lookupOrder={lookupOrder} />
      {err && <div style={{ color: 'var(--red)', fontSize: 12, marginTop: 10 }}>{err}</div>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
        <button className="btn" onClick={onClose} style={{ fontSize: 12 }}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={busy} style={{ fontSize: 12 }}>
          {busy ? <Loader size={12} className="spin" /> : null} Save to production
        </button>
      </div>
    </Modal>
  );
}

const NO = String.fromCharCode(35);

function ChargebackFields({ f, set, codes, statusLabels, moving, customer, orderHint, lookupOrder }) {
  const sel = (k, opts, blank) => (
    <select className="form-select" style={INPUT} value={f[k]} onChange={(e) => set(k, e.target.value)}>
      {blank !== undefined && <option value="">{blank}</option>}
      {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
  const inp = (k, extra) => (
    <input className="form-input" style={INPUT} value={f[k]} onChange={(e) => set(k, e.target.value)} {...(extra || {})} />
  );
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
      <Field label="Company">{sel('company', Object.entries(codes.companies || {}))}</Field>
      <Field label="Category (QuickBooks)">{sel('category', (codes.categories || []).map((c) => [c, c]), '(none yet)')}</Field>
      <Field label="Kind">{sel('kind', Object.entries(codes.kinds || {}))}</Field>
      <Field label="Customer" span={3}>{customer}</Field>
      <Field label={f.company === 'CC' ? 'C&C order ref' : 'Order ' + NO}>
        {inp('orderNumber', { onBlur: lookupOrder })}
        {orderHint && <div style={{ fontSize: 10.5, color: 'var(--gray-500)', marginTop: 2 }}>{orderHint}</div>}
      </Field>
      <Field label="Transaction id">{inp('transactionId')}</Field>
      <Field label="Disputed amount">{inp('amount', { inputMode: 'decimal' })}</Field>
      <Field label="Debit">{inp('amountDebited', { inputMode: 'decimal' })}</Field>
      <Field label="Credit">{inp('amountCredited', { inputMode: 'decimal' })}</Field>
      <Field label="Fraud">{sel('isFraud', [['Y', 'Yes'], ['N', 'No']], 'Unknown')}</Field>
      <Field label="Transaction date">{inp('transactionDate', { type: 'date' })}</Field>
      <Field label="Received date">{inp('receivedDate', { type: 'date' })}</Field>
      <Field label={'Case ' + NO}>{inp('caseNumber')}</Field>
      <Field label="Case type">
        {inp('caseType', { list: 'cb-case-types' })}
        <datalist id="cb-case-types">{(codes.caseTypes || []).map((t) => <option key={t} value={t} />)}</datalist>
      </Field>
      <Field label="Reason" span={2}>{inp('reason')}</Field>
      <Field label="Status">{sel('status', Object.entries(statusLabels))}</Field>
      {moving ? (
        <>
          <Field label="Follow-up">{sel('followUp', (codes.followUps || []).map((x) => [x, x]), DASH)}</Field>
          <Field label="Follow-up received">{inp('followUpDate', { type: 'date' })}</Field>
        </>
      ) : <div style={{ gridColumn: 'span 2' }} />}
      {f.company === 'CC' && (
        <div style={{ gridColumn: 'span 3', fontSize: 12.5 }}>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <input type="checkbox" checked={!!f.resold} onChange={(e) => set('resold', e.target.checked)} />
            Resold before the event {DASH} not a loss
          </label>
        </div>
      )}
      <Field label="Notes" span={3}>
        <textarea className="form-input" style={{ ...INPUT, minHeight: 60 }} value={f.notes} onChange={(e) => set('notes', e.target.value)} />
      </Field>
    </div>
  );
}

// A follow-up case line: the same dispute going round again (PreArb, a reversal, the customer
// challenging our rebuttal). It sits beneath the original and moves its current status.
function CaseForm({ parent, line, codes, onClose, onSaved }) {
  const [f, setF] = useState(() => ({
    chargebackCaseId: line ? line.chargeback_case_id : 0,
    chargebackId: parent.chargeback_id,
    caseNumber: line ? line.case_number || '' : '',
    caseType: line ? line.case_type || '' : '',
    receivedDate: line ? line.received_date || '' : todayIso(),
    status: line ? line.status : 'awaiting',
    amountDebited: line ? line.amount_debited ?? '' : '',
    amountCredited: line ? line.amount_credited ?? '' : '',
    followUp: line ? line.follow_up || '' : '',
    followUpDate: line ? line.follow_up_date || '' : '',
    notes: line ? line.notes || '' : '',
  }));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const moving = (codes.followUpStatuses || []).includes(f.status);
  const inp = (k, extra) => <input className="form-input" style={INPUT} value={f[k]} onChange={(e) => set(k, e.target.value)} {...(extra || {})} />;

  const save = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await neonQuery('chargeback_case_save', { ...f, repName: rep() });
      if (r && r.ok === false) { setErr(r.reason || 'Not saved.'); setBusy(false); return; }
      onSaved(r);
    } catch (e) { setErr(e.message || 'Not saved.'); setBusy(false); }
  };

  return (
    <Modal title={(line ? 'Edit follow-up case' : 'Add follow-up case') + ' - ' + (parent.customer_name || parent.email || 'chargeback ' + parent.chargeback_id)} onClose={onClose}>
      <div style={{ fontSize: 11.5, color: 'var(--gray-500)', marginBottom: 10 }}>
        Original: case {parent.case_number || DASH}, {money(parent.amount)}, received {day(parent.received_date)}.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
        <Field label={'Case ' + NO}>{inp('caseNumber')}</Field>
        <Field label="Case type">
          {inp('caseType', { list: 'cb-case-types-2' })}
          <datalist id="cb-case-types-2">{(codes.caseTypes || []).map((t) => <option key={t} value={t} />)}</datalist>
        </Field>
        <Field label="Received date">{inp('receivedDate', { type: 'date' })}</Field>
        <Field label="Status">
          <select className="form-select" style={INPUT} value={f.status} onChange={(e) => set('status', e.target.value)}>
            {Object.entries(codes.statusLabels || {}).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </Field>
        <Field label="Debit">{inp('amountDebited', { inputMode: 'decimal' })}</Field>
        <Field label="Credit (if won)">{inp('amountCredited', { inputMode: 'decimal' })}</Field>
        {moving && (
          <>
            <Field label="Follow-up">
              <select className="form-select" style={INPUT} value={f.followUp} onChange={(e) => set('followUp', e.target.value)}>
                <option value="">{DASH}</option>
                {(codes.followUps || []).map((x) => <option key={x} value={x}>{x}</option>)}
              </select>
            </Field>
            <Field label="Follow-up received">{inp('followUpDate', { type: 'date' })}</Field>
          </>
        )}
        <Field label="Notes" span={3}>
          <textarea className="form-input" style={{ ...INPUT, minHeight: 50 }} value={f.notes} onChange={(e) => set('notes', e.target.value)} />
        </Field>
      </div>
      {err && <div style={{ color: 'var(--red)', fontSize: 12, marginTop: 10 }}>{err}</div>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
        <button className="btn" onClick={onClose} style={{ fontSize: 12 }}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={busy} style={{ fontSize: 12 }}>
          {busy ? <Loader size={12} className="spin" /> : null} Save to production
        </button>
      </div>
    </Modal>
  );
}

// "Allow VIP again" needs a reason and the warning, every time. Blocking again only confirms.
function OverrideModal({ row, onClose, onSaved }) {
  const allow = !row.override_at;
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const go = async () => {
    if (allow && !note.trim()) { setErr('Say why this customer may be a VIP again.'); return; }
    setBusy(true); setErr(null);
    try {
      const r = await neonQuery('chargeback_set_override', { chargebackId: row.chargeback_id, allow, note, repName: rep() });
      if (r && r.ok === false) { setErr(r.reason || 'Not saved.'); setBusy(false); return; }
      onSaved(r);
    } catch (e) { setErr(e.message || 'Not saved.'); setBusy(false); }
  };
  return (
    <Modal title={allow ? 'Allow VIP again' : 'Block VIP again'} onClose={onClose} width={480}>
      <div style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--gray-700)' }}>
        <div style={{ fontWeight: 700, color: 'var(--navy)' }}>{row.customer_name || row.email}</div>
        {allow ? (
          <div style={{ margin: '8px 0', padding: '8px 10px', background: '#fff4e5', color: '#b54708', borderRadius: 6 }}>
            This lets a batch job or a rep make this customer a VIP again even though they charged back, and takes the Chargeback flag off them in Orders Summary and the win-back lists so they can be sent a VIP offer. Only do this on purpose.
          </div>
        ) : (
          <div style={{ margin: '8px 0' }}>
            Allowed by {row.override_by || 'someone'}{row.override_note ? ': "' + row.override_note + '"' : ''}. Blocking again stops them being made a VIP while this chargeback stands.
          </div>
        )}
        <div style={LABEL}>{allow ? 'Why (required)' : 'Note (optional)'}</div>
        <textarea className="form-input" style={{ ...INPUT, minHeight: 50 }} value={note} onChange={(e) => setNote(e.target.value)} />
      </div>
      {err && <div style={{ color: 'var(--red)', fontSize: 12, marginTop: 8 }}>{err}</div>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
        <button className="btn" onClick={onClose} style={{ fontSize: 12 }}>Cancel</button>
        <button className="btn btn-primary" onClick={go} disabled={busy} style={{ fontSize: 12 }}>{allow ? 'Allow VIP again' : 'Block VIP again'}</button>
      </div>
    </Modal>
  );
}

// Bottom-of-log totals for the current filter. Losses and credits are only chargebacks that count
// as a loss (server rule: not category VIP, not a resold C&C ticket); those two are shown on their
// own line so they are visible but never inflate the loss.
function Totals({ totals, companies, split }) {
  if (!totals || !totals.length) return null;
  const n = (v) => Number(v || 0);
  const sum = (k) => totals.reduce((s, t) => s + n(t[k]), 0);
  const all = {
    company: null, losses: sum('losses'), credits: sum('credits'), net: sum('net'),
    excluded_vip_count: sum('excluded_vip_count'), excluded_vip_debits: sum('excluded_vip_debits'), excluded_vip_credits: sum('excluded_vip_credits'),
    excluded_resold_count: sum('excluded_resold_count'), excluded_resold_debits: sum('excluded_resold_debits'), excluded_resold_credits: sum('excluded_resold_credits'),
  };
  const blocks = split && totals.length > 1 ? [...totals, all] : [totals.length === 1 ? totals[0] : all];
  return (
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', padding: '12px 16px', borderTop: '2px solid var(--gray-200)', background: 'var(--gray-50)' }}>
      {blocks.map((t) => {
        const net = n(t.net);
        const excluded = [];
        if (n(t.excluded_vip_count)) excluded.push(`VIP ${t.excluded_vip_count}: ${money(t.excluded_vip_debits)} debited, ${money(t.excluded_vip_credits)} credited`);
        if (n(t.excluded_resold_count)) excluded.push(`Resold C&C ${t.excluded_resold_count}: ${money(t.excluded_resold_debits)} debited, ${money(t.excluded_resold_credits)} credited`);
        return (
          <div key={t.company || 'all'} style={{ minWidth: 230, flex: '1 1 230px' }}>
            <div style={{ ...LABEL, color: 'var(--navy)' }}>{t.company ? (companies[t.company] || t.company) : (blocks.length > 1 ? 'Both companies' : 'Totals')}</div>
            <table style={{ fontSize: 12.5, borderCollapse: 'collapse' }}>
              <tbody>
                <tr><td style={{ paddingRight: 16 }}>Losses</td><td style={{ textAlign: 'right', fontWeight: 600 }}>{money(t.losses)}</td></tr>
                <tr><td style={{ paddingRight: 16 }}>Credits received</td><td style={{ textAlign: 'right', fontWeight: 600 }}>{money(t.credits)}</td></tr>
                <tr style={{ borderTop: '1px solid var(--gray-300)' }}>
                  <td style={{ paddingRight: 16, fontWeight: 800 }}>NET</td>
                  <td style={{ textAlign: 'right', fontWeight: 800, color: net < 0 ? '#b42318' : '#1e7b3a' }}>{money(net)}</td>
                </tr>
              </tbody>
            </table>
            {excluded.length > 0 && (
              <div style={{ fontSize: 10.5, color: 'var(--gray-500)', marginTop: 4 }}>Not counted as a loss: {excluded.join('; ')}</div>
            )}
          </div>
        );
      })}
    </div>
  );
}

const sel = (value, onChange, options) => (
  <select className="form-select" style={{ fontSize: 11 }} value={value} onChange={(e) => onChange(e.target.value)}>
    {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
  </select>
);

// ── Export to Excel ─────────────────────────────────────────────────────────────────────────────
// Rachael attaches the chargebacks sheet to her monthly report, so the export rebuilds HER layout
// (the "CP Chargebacks.xlsx" she sent 2026-10-13): one tab per year, one row per case number in
// received-date order, a "<Month> Total:" row after each month. Company and Category (Greg's
// QuickBooks accounts) go on the end, and the losses / credits / net block from the bottom of the
// log goes at the bottom.
//
// ⚠️ FOLLOW-UP CASES GO BACK TO THEIR OWN ROWS. The log keeps them under the original; her sheet has
// every case number as its own line on the day it arrived, with its own credit or debit. A line is
// placed by ITS received date, so the export reads every chargeback, not just the ones received in
// the range - a September reversal on a July chargeback belongs in September.

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const EXPORT_HEAD = ['Case ' + NO, 'Case Type', 'Customer Name', 'Order ' + NO, 'Transaction Date',
  'Received Date', 'Credits', 'Debits', 'Fraud', 'Chargeback Reason', 'Action Taken', 'Company', 'Category'];
const isoDay = (d) => d.toLocaleDateString('en-CA');
// Excel's day number for a yyyy-MM-dd, built in UTC so no timezone can move it a day.
const excelDay = (s) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s || ''));
  return m ? (Date.UTC(+m[1], +m[2] - 1, +m[3]) - Date.UTC(1899, 11, 30)) / 86400000 : '';
};
const mag = (v) => (has(v) ? Math.abs(Number(v)) : 0);
// Currency with negatives in red; written as text so no shell or template ever reads the dollar sign.
const CURRENCY = '"' + String.fromCharCode(36) + '"#,##0.00;[Red]-"' + String.fromCharCode(36) + '"#,##0.00';

// [from, to] for the chosen range. Last month and last quarter are the last COMPLETE ones.
function exportRange(mode, year, from, to) {
  const now = new Date();
  if (mode === 'month') return [isoDay(new Date(now.getFullYear(), now.getMonth() - 1, 1)), isoDay(new Date(now.getFullYear(), now.getMonth(), 0))];
  if (mode === 'quarter') {
    const q = Math.floor(now.getMonth() / 3);
    return [isoDay(new Date(now.getFullYear(), (q - 1) * 3, 1)), isoDay(new Date(now.getFullYear(), q * 3, 0))];
  }
  if (mode === 'custom') return [from, to];
  return [year + '-01-01', year + '-12-31'];
}

// One line per case number: the original, then each follow-up, which carries the original's
// customer, order, reason, company and category (her sheet repeats them on every line).
function exportLines(data) {
  const casesBy = {};
  for (const c of data.cases || []) (casesBy[c.chargeback_id] = casesBy[c.chargeback_id] || []).push(c);
  const out = [];
  for (const r of data.chargebacks || []) {
    const shared = {
      customer: r.customer_name || r.email || '', order: String(r.order_number || r.order_id || ''),
      transaction: r.transaction_date, reason: r.reason || '',
      fraud: r.is_fraud === true ? 'Y' : r.is_fraud === false ? 'N' : '',
      company: r.company === 'CC' ? 'C&C' : 'Crab Place', category: r.category || '',
      // Same rule as the log totals (server-decided): VIP and a resold C&C ticket are not a loss.
      loss: r.loss !== false,
    };
    out.push({ ...shared, caseNumber: r.case_number || '', caseType: r.case_type || '', received: r.received_date || r.transaction_date,
      credit: mag(r.amount_credited), debit: mag(r.amount_debited), status: r.status, followUp: r.follow_up });
    for (const c of casesBy[r.chargeback_id] || []) {
      out.push({ ...shared, caseNumber: c.case_number || '', caseType: c.case_type || '', received: c.received_date || r.received_date || r.transaction_date,
        credit: mag(c.amount_credited), debit: mag(c.amount_debited), status: c.status, followUp: c.follow_up });
    }
  }
  return out;
}

async function downloadWorkbook(lines, from, to, labels) {
  const XLSX = await import('xlsx');
  const picked = lines.filter((l) => l.received && l.received >= from && l.received <= to)
    .sort((a, b) => (a.received < b.received ? -1 : a.received > b.received ? 1 : 0));
  const wb = XLSX.utils.book_new();
  // Label in column A, money in G (Credits) and H (Debits).
  const pad = ['', '', '', '', ''];
  for (const year of [...new Set(picked.map((l) => l.received.slice(0, 4)))]) {
    const ys = picked.filter((l) => l.received.slice(0, 4) === year);
    const aoa = [EXPORT_HEAD];
    let month = null; let mc = 0; let md = 0;
    const monthTotal = () => aoa.push([MONTHS[month - 1] + ' Total:', ...pad, mc, md]);
    for (const l of ys) {
      const m = Number(l.received.slice(5, 7));
      if (month !== null && m !== month) { monthTotal(); mc = 0; md = 0; }
      month = m; mc += l.credit; md += l.debit;
      aoa.push([l.caseNumber, l.caseType, l.customer, l.order, excelDay(l.transaction), excelDay(l.received),
        l.credit || '', l.debit || '', l.fraud, l.reason,
        (labels[l.status] || l.status || '') + (l.followUp ? ' - ' + l.followUp : ''), l.company, l.category]);
    }
    monthTotal();
    const sum = (rows, k) => rows.reduce((s, l) => s + l[k], 0);
    const loss = ys.filter((l) => l.loss);
    const notLoss = ys.filter((l) => !l.loss);
    aoa.push([]);
    aoa.push(['Total', ...pad, sum(ys, 'credit'), sum(ys, 'debit')]);
    aoa.push([]);
    aoa.push(['Losses', ...pad, '', sum(loss, 'debit')]);
    aoa.push(['Credits received', ...pad, sum(loss, 'credit')]);
    aoa.push(['NET (credits - losses)', ...pad, sum(loss, 'credit') - sum(loss, 'debit')]);
    if (notLoss.length) aoa.push(['Not counted as a loss (VIP / resold C&C)', ...pad, sum(notLoss, 'credit'), sum(notLoss, 'debit')]);
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    const range = XLSX.utils.decode_range(ws['!ref']);
    for (let R = 1; R <= range.e.r; R++) {
      for (const C of [4, 5, 6, 7]) {
        const cell = ws[XLSX.utils.encode_cell({ r: R, c: C })];
        if (cell && cell.t === 'n') cell.z = C < 6 ? 'm/d/yyyy' : CURRENCY;
      }
    }
    ws['!cols'] = [16, 26, 22, 12, 12, 12, 12, 12, 6, 34, 26, 11, 12].map((wch) => ({ wch }));
    XLSX.utils.book_append_sheet(wb, ws, year);
  }
  XLSX.writeFile(wb, 'Chargebacks ' + from + ' to ' + to + '.xlsx');
}

function ExportModal({ onClose }) {
  const thisYear = String(new Date().getFullYear());
  const [mode, setMode] = useState('year');
  const [year, setYear] = useState(thisYear);
  const [from, setFrom] = useState(yearStart());
  const [to, setTo] = useState(todayIso());
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let live = true;
    neonQuery('chargebacks_page', { from: '', to: '' })
      .then((d) => { if (live) setData(d); })
      .catch((e) => { if (live) setErr(e.message); });
    return () => { live = false; };
  }, []);
  const lines = useMemo(() => (data ? exportLines(data) : []), [data]);
  const years = useMemo(() => {
    const s = new Set(lines.filter((l) => l.received).map((l) => l.received.slice(0, 4)));
    s.add(thisYear);
    return [...s].sort().reverse();
  }, [lines, thisYear]);
  const [rf, rt] = exportRange(mode, year, from, to);
  const count = lines.filter((l) => l.received && l.received >= rf && l.received <= rt).length;
  const undated = lines.filter((l) => !l.received).length;
  // page() caps the log at 2000 chargebacks; say so rather than export a silently short sheet.
  const capped = data && (data.chargebacks || []).length >= 2000;

  const go = async () => {
    setBusy(true); setErr(null);
    try { await downloadWorkbook(lines, rf, rt, (data.codes && data.codes.statusLabels) || {}); onClose(); }
    catch (e) { setErr(e.message || 'Could not build the spreadsheet.'); setBusy(false); }
  };
  const radio = (id, label) => (
    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, cursor: 'pointer', padding: '3px 0' }}>
      <input type="radio" name="cb-export-range" checked={mode === id} onChange={() => setMode(id)} /> {label}
    </label>
  );

  return (
    <Modal title="Export to Excel" onClose={onClose} width={460}>
      <div style={{ fontSize: 11.5, color: 'var(--gray-500)', marginBottom: 10 }}>
        Same layout as the CP Chargebacks spreadsheet: one tab per year, every case number on its own row, a total after each month, losses / credits / net at the bottom. Removed chargebacks are left out.
      </div>
      <div style={LABEL}>Range</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {radio('year', 'Year')}
        {mode === 'year' && (
          <select className="form-select" style={{ fontSize: 11, width: 90 }} value={year} onChange={(e) => setYear(e.target.value)}>
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        )}
      </div>
      {radio('month', 'Last month')}
      {radio('quarter', 'Last quarter')}
      {radio('custom', 'Custom')}
      {mode === 'custom' && (
        <div style={{ display: 'flex', gap: 8, margin: '4px 0 0 22px' }}>
          <input type="date" className="form-input" style={{ fontSize: 11 }} value={from} onChange={(e) => setFrom(e.target.value)} />
          <input type="date" className="form-input" style={{ fontSize: 11 }} value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
      )}
      <div style={{ fontSize: 12, marginTop: 12 }}>
        {!data && !err ? <span style={{ color: 'var(--gray-500)' }}><Loader size={12} className="spin" style={{ verticalAlign: -2 }} /> Loading...</span>
          : <span><b>{count}</b> case {count === 1 ? 'line' : 'lines'}, {day(rf)} to {day(rt)}</span>}
      </div>
      {undated > 0 && <div style={{ fontSize: 11, color: '#b54708', marginTop: 4 }}>{undated} case {undated === 1 ? 'line has' : 'lines have'} no date and cannot go in any range.</div>}
      {capped && <div style={{ fontSize: 11, color: '#b54708', marginTop: 4 }}>Only the latest 2000 chargebacks were read; older ones may be missing.</div>}
      {err && <div style={{ color: 'var(--red)', fontSize: 12, marginTop: 8 }}>{err}</div>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
        <button className="btn" onClick={onClose} style={{ fontSize: 12 }}>Cancel</button>
        <button className="btn btn-primary" onClick={go} disabled={busy || !data || !count || rf > rt} style={{ fontSize: 12 }}>
          {busy ? <Loader size={12} className="spin" /> : <Download size={12} style={{ verticalAlign: -2 }} />} Download
        </button>
      </div>
    </Modal>
  );
}
export default function Chargebacks({ navigateTo }) {
  const [tab, setTab] = useState('log');
  const [filters, setFilters] = useState({
    company: '', category: '', status: '', kind: '', from: yearStart(), to: todayIso(), search: '', showRemoved: false,
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [form, setForm] = useState(null);
  const [caseForm, setCaseForm] = useState(null);
  const [override, setOverride] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [openNotes, setOpenNotes] = useState({});
  const [toast, setToast] = useState(null);
  const setFilter = (k, v) => setFilters((p) => ({ ...p, [k]: v }));

  const load = useCallback(async (fl) => {
    setLoading(true); setErr(null);
    try {
      setData(await neonQuery('chargebacks_page', fl));
    } catch (e) { setErr(e.message); }
    setLoading(false);
  }, []);
  useEffect(() => { load(filters); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const codes = (data && data.codes) || {};
  const labels = codes.statusLabels || {};
  const companies = codes.companies || {};
  const casesBy = useMemo(() => {
    const m = {};
    for (const c of (data && data.cases) || []) (m[c.chargeback_id] = m[c.chargeback_id] || []).push(c);
    return m;
  }, [data]);

  // Every write re-pulls the production mirror before answering; mirrored:false means production
  // has it but the mirror refresh failed, so say when it will show rather than look unsaved.
  const afterSave = (r) => {
    setForm(null); setCaseForm(null); setOverride(null);
    setToast(r && r.mirrored === false
      ? 'Saved in production. The list will show it within 15 minutes.'
      : 'Saved.');
    setTimeout(() => setToast(null), 5000);
    load(filters);
  };
  const run = async (query, params) => {
    try {
      const r = await neonQuery(query, { ...params, repName: rep() });
      if (r && r.ok === false) { setErr(r.reason || 'Not saved.'); return; }
      afterSave(r);
    } catch (e) { setErr(e.message); }
  };
  const removeRow = (row) => {
    const reason = window.prompt('Remove the chargeback on ' + (row.customer_name || row.email || 'this customer') + '? Why? (e.g. entered on the wrong customer)', '');
    if (reason === null) return;
    if (!reason.trim()) { setErr('Say why it is being removed.'); return; }
    run('chargeback_delete', { chargebackId: row.chargeback_id, reason });
  };
  const removeCase = (line) => {
    const reason = window.prompt('Remove this follow-up case line? Why?', '');
    if (reason === null) return;
    if (!reason.trim()) { setErr('Say why it is being removed.'); return; }
    run('chargeback_case_delete', { chargebackCaseId: line.chargeback_case_id, reason });
  };

  // Crab Place: the printed BC order number opens Order Details (it looks orders up by BC number,
  // falling back to the internal id). C&C orders have no view in CPx or CNCx, so the ref is text.
  const orderCell = (row) => {
    const label = row.order_number || row.order_id;
    if (!label) return DASH;
    if (row.company !== 'CP' || !navigateTo) return <span>{label}</span>;
    return (
      <button onClick={() => navigateTo('order-details', String(row.order_number || row.order_id))}
        style={{ ...LINK_BTN, color: 'var(--orange)', fontWeight: 600, fontSize: 'inherit', fontFamily: 'inherit' }}>{label}</button>
    );
  };

  const toggleNote = (key) => setOpenNotes((p) => ({ ...p, [key]: !p[key] }));
  const noteBtn = (key, notes) => (notes ? (
    <button title={openNotes[key] ? 'Hide note' : 'Show note'} onClick={() => toggleNote(key)} style={{ ...LINK_BTN, color: openNotes[key] ? 'var(--orange)' : 'var(--gray-500)' }}>
      <StickyNote size={13} />
    </button>
  ) : null);

  const rows = (data && data.chargebacks) || [];
  const COLS = 16;

  const renderRow = (row, i) => {
    const key = 'cb' + row.chargeback_id;
    const cur = row.current_status || row.status;
    const removed = !!row.deleted_at;
    const lines = casesBy[row.chargeback_id] || [];
    const bg = removed ? '#f6f6f6' : (i % 2 === 0 ? '#fff' : 'var(--gray-50)');
    const out = [
      <tr key={key} style={{ borderTop: '1px solid var(--gray-200)', background: bg, opacity: removed ? 0.6 : 1 }}>
        <td style={{ ...TD, fontWeight: 600, color: 'var(--navy)', whiteSpace: 'nowrap' }}>{row.case_number || DASH}</td>
        <td style={TD}>{row.case_type || DASH}</td>
        <td style={TD}>
          <div style={{ fontWeight: 600 }}>{row.customer_name || DASH}</div>
          {row.email && <div style={{ fontSize: 10.5, color: 'var(--gray-500)' }}>{row.email}</div>}
        </td>
        <td style={TD}>{orderCell(row)}</td>
        <td style={{ ...TD, whiteSpace: 'nowrap' }}>{day(row.transaction_date)}</td>
        <td style={{ ...TD, whiteSpace: 'nowrap' }}>{day(row.received_date)}</td>
        <td style={{ ...TD, textAlign: 'right', whiteSpace: 'nowrap', color: has(row.amount_credited) ? '#1e7b3a' : 'var(--gray-400)' }}>
          {has(row.amount_credited) ? money(row.amount_credited) : DASH}
          {lines.length > 0 && has(row.total_credited) && Number(row.total_credited) !== Number(row.amount_credited || 0) && (
            <div style={{ fontSize: 10, color: 'var(--gray-500)' }}>all lines {money(row.total_credited)}</div>
          )}
        </td>
        <td style={{ ...TD, textAlign: 'right', whiteSpace: 'nowrap', color: has(row.amount_debited) ? '#b42318' : 'var(--gray-400)' }}>
          {has(row.amount_debited) ? money(row.amount_debited) : DASH}
          {lines.length > 0 && has(row.total_debited) && Number(row.total_debited) !== Number(row.amount_debited || 0) && (
            <div style={{ fontSize: 10, color: 'var(--gray-500)' }}>all lines {money(row.total_debited)}</div>
          )}
          {has(row.amount) && <div style={{ fontSize: 10, color: 'var(--gray-500)' }}>disputed {money(row.amount)}</div>}
        </td>
        <td style={TD}>{row.is_fraud === true ? 'Y' : row.is_fraud === false ? 'N' : DASH}</td>
        <td style={{ ...TD, maxWidth: 180 }}>{row.reason || DASH}</td>
        <td style={TD}>
          <Badge code={cur} labels={labels} />
          {cur !== row.status && <div style={{ fontSize: 10, color: 'var(--gray-500)', marginTop: 3 }}>original: {labels[row.status] || row.status}</div>}
          {row.follow_up && <div style={{ fontSize: 10, color: 'var(--gray-500)', marginTop: 3 }}>{row.follow_up} {day(row.follow_up_date)}</div>}
        </td>
        <td style={TD}>{row.company === 'CC' ? 'C&C' : row.company}</td>
        <td style={TD}>{row.category || DASH}</td>
        <td style={{ ...TD, minWidth: 110 }}>{flagsFor(row, removed)}</td>
        <td style={TD}>{noteBtn(key, row.notes)}</td>
        <td style={{ ...TD, whiteSpace: 'nowrap' }}>{removed ? null : actionsFor(row)}</td>
      </tr>,
    ];
    if (openNotes[key] && row.notes) {
      out.push(
        <tr key={key + 'n'} style={{ background: bg }}>
          <td colSpan={COLS} style={{ padding: '0 8px 8px 24px', fontSize: 11.5, color: 'var(--gray-700)', whiteSpace: 'pre-wrap' }}>{row.notes}</td>
        </tr>
      );
    }
    for (const c of lines) {
      const ck = 'case' + c.chargeback_case_id;
      const cRemoved = !!c.deleted_at;
      out.push(
        <tr key={ck} style={{ background: bg, fontSize: 11, color: 'var(--gray-600)', opacity: cRemoved ? 0.55 : 1 }}>
          <td style={{ ...TD, paddingLeft: 18, whiteSpace: 'nowrap' }}>
            <CornerDownRight size={11} style={{ verticalAlign: -1, marginRight: 4, color: 'var(--gray-400)' }} />{c.case_number || DASH}
          </td>
          <td style={TD} colSpan={4}>{cRemoved ? <Flag>Removed</Flag> : null}</td>
          <td style={{ ...TD, whiteSpace: 'nowrap' }}>{day(c.received_date)}</td>
          <td style={{ ...TD, textAlign: 'right', color: has(c.amount_credited) ? '#1e7b3a' : 'var(--gray-400)' }}>{has(c.amount_credited) ? money(c.amount_credited) : DASH}</td>
          <td style={{ ...TD, textAlign: 'right', color: has(c.amount_debited) ? '#b42318' : 'var(--gray-400)' }}>{has(c.amount_debited) ? money(c.amount_debited) : DASH}</td>
          <td style={TD} colSpan={2} />
          <td style={TD}>
            <Badge code={c.status} labels={labels} small />
            {c.follow_up && <div style={{ fontSize: 10, marginTop: 3 }}>{c.follow_up} {day(c.follow_up_date)}</div>}
          </td>
          <td style={TD} colSpan={3} />
          <td style={TD}>{noteBtn(ck, c.notes)}</td>
          <td style={{ ...TD, whiteSpace: 'nowrap' }}>
            {!cRemoved && !removed && (
              <>
                <button title="Edit follow-up case" style={LINK_BTN} onClick={() => setCaseForm({ parent: row, line: c })}><Pencil size={12} /></button>
                <button title="Remove follow-up case" style={LINK_BTN} onClick={() => removeCase(c)}><Trash2 size={12} /></button>
              </>
            )}
          </td>
        </tr>
      );
      if (openNotes[ck] && c.notes) {
        out.push(
          <tr key={ck + 'n'} style={{ background: bg }}>
            <td colSpan={COLS} style={{ padding: '0 8px 8px 40px', fontSize: 11, color: 'var(--gray-700)', whiteSpace: 'pre-wrap' }}>{c.notes}</td>
          </tr>
        );
      }
    }
    return out;
  };

  const flagsFor = (row, removed) => (
    <>
      {removed && <Flag title={row.deleted_by ? 'Removed by ' + row.deleted_by : undefined}>Removed</Flag>}
      {row.blocks_vip && !removed && <Flag tone="red" title="Production will not reinstate or auto-renew this customer">Blocks VIP</Flag>}
      {row.override_at && <Flag tone="amber" title={'Allowed by ' + (row.override_by || 'unknown') + (row.override_note ? ': ' + row.override_note : '')}>VIP allowed</Flag>}
      {row.resold && <Flag tone="green">Resold {DASH} not a loss</Flag>}
      {row.repeat_customer && <Flag tone="amber" title="This customer has more than one chargeback">Repeat ({row.customer_chargeback_count})</Flag>}
      {!row.customer_id && (
        <Flag title={row.kind === 'membership' ? 'Cannot block VIP until a customer is linked. Edit to link one.' : 'Edit to link a customer.'}>No customer linked</Flag>
      )}
    </>
  );

  const actionsFor = (row) => (
    <>
      <button title="Edit" style={LINK_BTN} onClick={() => setForm({ row })}><Pencil size={13} /></button>
      <button title="Add follow-up case" style={LINK_BTN} onClick={() => setCaseForm({ parent: row, line: null })}><Plus size={14} /></button>
      {row.kind === 'membership' && row.customer_id && (
        <button title={row.override_at ? 'Block VIP again' : 'Allow VIP again'} style={LINK_BTN} onClick={() => setOverride(row)}>
          {row.override_at ? <ShieldCheck size={13} /> : <ShieldOff size={13} />}
        </button>
      )}
      <button title="Remove" style={LINK_BTN} onClick={() => removeRow(row)}><Trash2 size={13} /></button>
    </>
  );

  const tabBtn = (id, label) => (
    <button onClick={() => setTab(id)} className={tab === id ? 'btn btn-primary' : 'btn'} style={{ fontSize: 11.5 }}>{label}</button>
  );

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>Chargebacks</h2>
          <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 2 }}>
            Every card dispute, Crab Place and Crab &amp; Cruise. Saved straight to production; a membership chargeback blocks VIP until it is won.
            {data && data.syncedAt ? ' Mirror refreshed ' + new Date(data.syncedAt).toLocaleString() + '.' : ''}
          </div>
        </div>
        {tabBtn('log', 'Log')}
        {tabBtn('reports', 'Reports')}
        <button className="btn" style={{ fontSize: 11.5 }} onClick={() => setExporting(true)}>
          <Download size={12} style={{ verticalAlign: -2 }} /> Export to Excel
        </button>
        {tab === 'log' && (
          <button className="btn btn-navy" style={{ fontSize: 11.5 }} onClick={() => setForm({ row: null })} disabled={!data}>
            <Plus size={12} style={{ verticalAlign: -2 }} /> Add chargeback
          </button>
        )}
      </div>

      {toast && <div style={{ marginBottom: 10, padding: '8px 12px', background: '#e7f6ec', color: '#1e7b3a', borderRadius: 6, fontSize: 12 }}>{toast}</div>}
      {err && (
        <div style={{ marginBottom: 10, padding: '8px 12px', background: '#fdecea', color: '#b42318', borderRadius: 6, fontSize: 12, display: 'flex' }}>
          <span style={{ flex: 1 }}>{err}</span>
          <button style={{ ...LINK_BTN, color: '#b42318' }} onClick={() => setErr(null)}><X size={14} /></button>
        </div>
      )}

      {tab === 'reports' ? <Reports codes={codes} /> : (
        <>
          <div className="card" style={{ marginBottom: 12 }}>
            <div style={{ padding: '12px 16px', display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div><div style={LABEL}>Company</div>{sel(filters.company, (v) => setFilter('company', v), [['', 'Both'], ['CP', 'Crab Place'], ['CC', 'Crab & Cruise']])}</div>
              <div><div style={LABEL}>Category</div>{sel(filters.category, (v) => setFilter('category', v), [['', 'All'], ...(codes.categories || []).map((c) => [c, c]), ['(none)', '(none yet)']])}</div>
              <div><div style={LABEL}>Status</div>{sel(filters.status, (v) => setFilter('status', v), [['', 'All'], ...Object.entries(labels)])}</div>
              <div><div style={LABEL}>Kind</div>{sel(filters.kind, (v) => setFilter('kind', v), [['', 'All'], ...Object.entries(codes.kinds || {})])}</div>
              <div><div style={LABEL}>From</div><input type="date" className="form-input" style={{ fontSize: 11 }} value={filters.from} onChange={(e) => setFilter('from', e.target.value)} /></div>
              <div><div style={LABEL}>To</div><input type="date" className="form-input" style={{ fontSize: 11 }} value={filters.to} onChange={(e) => setFilter('to', e.target.value)} /></div>
              <div style={{ flex: '1 1 180px' }}>
                <div style={LABEL}>Search</div>
                <input className="form-input" style={{ fontSize: 11, width: '100%' }} value={filters.search} placeholder="Name, email, case, order, transaction"
                  onChange={(e) => setFilter('search', e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') load(filters); }} />
              </div>
              <label style={{ fontSize: 11.5, display: 'inline-flex', alignItems: 'center', gap: 5, paddingBottom: 6 }}>
                <input type="checkbox" checked={filters.showRemoved} onChange={(e) => setFilter('showRemoved', e.target.checked)} /> Show removed
              </label>
              <button className="btn btn-primary" style={{ fontSize: 11 }} onClick={() => load(filters)}>
                {loading ? <Loader size={12} className="spin" /> : <RefreshCw size={12} style={{ verticalAlign: -2 }} />} Search
              </button>
            </div>
          </div>

          <div className="card">
            <div className="card-header">Log {rows.length > 0 && '(' + rows.length + ')'}</div>
            {loading && !data && <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>Loading...</div>}
            {data && rows.length === 0 && <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>No chargebacks match.</div>}
            {rows.length > 0 && (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', fontSize: 11.5, borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                      <th style={TH}>{'Case ' + NO}</th><th style={TH}>Case type</th><th style={TH}>Customer</th><th style={TH}>{'Order ' + NO}</th>
                      <th style={TH}>Transaction</th><th style={TH}>Received</th>
                      <th style={{ ...TH, textAlign: 'right' }}>Credits</th><th style={{ ...TH, textAlign: 'right' }}>Debits</th>
                      <th style={TH}>Fraud</th><th style={TH}>Reason</th><th style={TH}>Status</th><th style={TH}>Company</th>
                      <th style={TH}>Category</th><th style={TH}>Flags</th><th style={TH} title="Notes" /><th style={TH} />
                    </tr>
                  </thead>
                  <tbody>{rows.map((r, i) => renderRow(r, i))}</tbody>
                </table>
              </div>
            )}
            {data && <Totals totals={data.totals} companies={companies} split={!filters.company} />}
          </div>
        </>
      )}

      {form && <ChargebackForm row={form.row} codes={codes} onClose={() => setForm(null)} onSaved={afterSave} />}
      {caseForm && <CaseForm parent={caseForm.parent} line={caseForm.line} codes={codes} onClose={() => setCaseForm(null)} onSaved={afterSave} />}
      {exporting && <ExportModal onClose={() => setExporting(false)} />}
      {override && <OverrideModal row={override} onClose={() => setOverride(null)} onSaved={afterSave} />}
    </div>
  );
}

// Reports: by month (and company), by QuickBooks category, and customers with more than one.
// Same loss rule as the log totals; the category table is every account, VIP included, because it
// is what gets posted - not a loss view.
function Reports({ codes }) {
  const [f, setF] = useState({ company: '', from: yearStart(), to: todayIso() });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const companies = codes.companies || {};
  const load = useCallback(async (p) => {
    setLoading(true); setErr(null);
    try { setData(await neonQuery('chargebacks_reports', p)); } catch (e) { setErr(e.message); }
    setLoading(false);
  }, []);
  useEffect(() => { load(f); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const co = (c) => (c === 'CC' ? 'C&C' : c);
  const netCell = (v) => <td style={{ ...TD, textAlign: 'right', fontWeight: 700, color: Number(v) < 0 ? '#b42318' : '#1e7b3a' }}>{money(v)}</td>;
  const num = (v) => <td style={{ ...TD, textAlign: 'right' }}>{money(v)}</td>;
  const table = (title, head, body) => (
    <div className="card" style={{ marginBottom: 12 }}>
      <div className="card-header">{title}</div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', fontSize: 11.5, borderCollapse: 'collapse' }}>
          <thead><tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>{head}</tr></thead>
          <tbody>{body}</tbody>
        </table>
      </div>
    </div>
  );
  const rowStyle = (i) => ({ borderTop: '1px solid var(--gray-100)', background: i % 2 ? 'var(--gray-50)' : '#fff' });

  return (
    <div>
      <div className="card" style={{ marginBottom: 12 }}>
        <div style={{ padding: '12px 16px', display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div><div style={LABEL}>Company</div>{sel(f.company, (v) => setF((p) => ({ ...p, company: v })), [['', 'Both'], ...Object.entries(companies)])}</div>
          <div><div style={LABEL}>From</div><input type="date" className="form-input" style={{ fontSize: 11 }} value={f.from} onChange={(e) => setF((p) => ({ ...p, from: e.target.value }))} /></div>
          <div><div style={LABEL}>To</div><input type="date" className="form-input" style={{ fontSize: 11 }} value={f.to} onChange={(e) => setF((p) => ({ ...p, to: e.target.value }))} /></div>
          <button className="btn btn-primary" style={{ fontSize: 11 }} onClick={() => load(f)}>{loading ? <Loader size={12} className="spin" /> : null} Run</button>
        </div>
      </div>
      {err && <div style={{ color: 'var(--red)', fontSize: 12, marginBottom: 10 }}>{err}</div>}
      {data && (
        <>
          {table('By month',
            <><th style={TH}>Month</th><th style={TH}>Company</th><th style={{ ...TH, textAlign: 'right' }}>Count</th>
              <th style={{ ...TH, textAlign: 'right' }}>Losses</th><th style={{ ...TH, textAlign: 'right' }}>Credits</th>
              <th style={{ ...TH, textAlign: 'right' }}>Net</th><th style={{ ...TH, textAlign: 'right' }}>Not a loss (VIP / resold)</th></>,
            data.byMonth.length ? data.byMonth.map((r, i) => (
              <tr key={r.month + r.company} style={rowStyle(i)}>
                <td style={TD}>{r.month || DASH}</td><td style={TD}>{co(r.company)}</td>
                <td style={{ ...TD, textAlign: 'right' }}>{r.chargebacks}</td>
                {num(r.losses)}{num(r.credits)}{netCell(r.net)}
                <td style={{ ...TD, textAlign: 'right', color: 'var(--gray-500)' }}>
                  {Number(r.excluded_vip_count) + Number(r.excluded_resold_count) > 0
                    ? (Number(r.excluded_vip_count) + Number(r.excluded_resold_count)) + ': ' + money(Number(r.excluded_vip_debits) + Number(r.excluded_resold_debits))
                    : DASH}
                </td>
              </tr>
            )) : <tr><td colSpan={7} style={{ ...TD, color: 'var(--gray-500)' }}>Nothing in this range.</td></tr>)}
          {table('By category (QuickBooks)',
            <><th style={TH}>Category</th><th style={TH}>Company</th><th style={{ ...TH, textAlign: 'right' }}>Count</th>
              <th style={{ ...TH, textAlign: 'right' }}>Debited</th><th style={{ ...TH, textAlign: 'right' }}>Credited</th><th style={{ ...TH, textAlign: 'right' }}>Net</th></>,
            data.byCategory.length ? data.byCategory.map((r, i) => (
              <tr key={r.category + r.company} style={rowStyle(i)}>
                <td style={TD}>{r.category}</td><td style={TD}>{co(r.company)}</td>
                <td style={{ ...TD, textAlign: 'right' }}>{r.chargebacks}</td>
                {num(r.debited)}{num(r.credited)}{netCell(r.net)}
              </tr>
            )) : <tr><td colSpan={6} style={{ ...TD, color: 'var(--gray-500)' }}>Nothing in this range.</td></tr>)}
          {table('Customers with more than one chargeback',
            <><th style={TH}>Customer</th><th style={TH}>Company</th><th style={{ ...TH, textAlign: 'right' }}>Chargebacks</th>
              <th style={{ ...TH, textAlign: 'right' }}>Debited</th><th style={{ ...TH, textAlign: 'right' }}>Credited</th>
              <th style={TH}>First</th><th style={TH}>Latest</th><th style={TH}>Cases</th></>,
            data.repeatCustomers.length ? data.repeatCustomers.map((r, i) => (
              <tr key={r.customer_key} style={rowStyle(i)}>
                <td style={TD}><b>{r.customer_name || DASH}</b>{r.email && <div style={{ fontSize: 10.5, color: 'var(--gray-500)' }}>{r.email}</div>}</td>
                <td style={TD}>{String(r.companies || '').replace('CC', 'C&C')}</td>
                <td style={{ ...TD, textAlign: 'right', fontWeight: 700 }}>{r.chargebacks}</td>
                {num(r.debited)}{num(r.credited)}
                <td style={TD}>{day(r.first_date)}</td><td style={TD}>{day(r.last_date)}</td>
                <td style={{ ...TD, fontSize: 10.5, color: 'var(--gray-500)' }}>{r.case_numbers}</td>
              </tr>
            )) : <tr><td colSpan={8} style={{ ...TD, color: 'var(--gray-500)' }}>No repeat customers in this range.</td></tr>)}
        </>
      )}
    </div>
  );
}
